// Dates arrive as epoch-second strings, ISO strings, or numbers.
export const EMPTY_VALUE_PLACEHOLDER = "—";

// UTC: ISO date-only is UTC midnight, so local would render Jan 14 west of UTC.
const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "UTC"
});

// Not Infinity, which would make two nulls compare as NaN and corrupt the sort.
const NULL_SORT_KEY = Number.MAX_SAFE_INTEGER;

const toText = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value).trim();

// new Date("1781151838") is Invalid and new Date(1781151838) is 1970, so
// all-digit text is treated as epoch seconds.
export const parseNrtDate = (value: unknown): Date | null => {
  const text = toText(value);
  if (text === "") {
    return null;
  }

  const date = /^-?\d+$/.test(text)
    ? new Date(Number(text) * 1000)
    : new Date(text);

  return Number.isNaN(date.getTime()) ? null : date;
};

// Unparseable-but-present values fall through as text, so bad data stays
// visible instead of looking absent or throwing.
export const formatNrtDate = (value: unknown): string => {
  const date = parseNrtDate(value);
  return date === null
    ? toText(value) || EMPTY_VALUE_PLACEHOLDER
    : dateFormatter.format(date);
};

// Parsed, not compared as text: that would order by month name and interleave
// epoch rows with ISO rows.
export const compareNrtDates = (a: unknown, b: unknown): number =>
  (parseNrtDate(a)?.getTime() ?? NULL_SORT_KEY) -
  (parseNrtDate(b)?.getTime() ?? NULL_SORT_KEY);

// Inverse of toDateInputValue: the API stores expirationDate as an
// epoch-second string, but a type="date" input only ever hands back
// "YYYY-MM-DD". Parsed as UTC midnight so round-tripping through the form
// doesn't shift the date a day in either direction across time zones.
export const toUnixTimestampString = (dateInputValue: string): string => {
  const utcMidnight = new Date(`${dateInputValue}T00:00:00Z`);
  return String(Math.floor(utcMidnight.getTime() / 1000));
};
